import { useEffect, useRef } from "react";

import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";

interface ShadowHtmlProps {
  html: string;
}

// Closes the reverse-tabnabbing gap: target is only meaningful on <a>/<area> here, so strip it
// everywhere else, and force noopener/noreferrer wherever it opens a new browsing context.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName !== "A" && node.tagName !== "AREA") {
    node.removeAttribute("target");
    return;
  }
  const target = node.getAttribute("target");
  if (target && target !== "_self") {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

// Renders sanitized HTML inside a Shadow DOM so its own <style> rules are isolated from (and can't clash with) the rest of the app.
export default function ShadowHtml({ html }: ShadowHtmlProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const shadowRef = useRef<ShadowRoot | null>(null);
  const navigate = useNavigate();
  // The click listener below is attached once and kept for the component's lifetime, so it closes
  // over this ref (always current) rather than `navigate` directly to avoid a stale closure.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!hostRef.current) return;
    if (!shadowRef.current) {
      shadowRef.current = hostRef.current.attachShadow({ mode: "open" });
      // Listening on the shadow root itself (rather than the light-DOM host) keeps this to a plain,
      // same-tree click/bubble - no reliance on a `composed` event crossing the shadow boundary.
      // ShadowRootEventMap only declares "slotchange", so "click" falls to the generic Event-typed
      // overload - cast is safe, a native "click" is always a MouseEvent.
      shadowRef.current.addEventListener("click", handleShadowClick as EventListener);
    }
    // FORCE_BODY stops the HTML parser treating a leading <style> as head-only content and dropping it.
    // ADD_ATTR re-allows target (stripped by default) since the afterSanitizeAttributes hook makes it safe.
    shadowRef.current.innerHTML = DOMPurify.sanitize(html, { FORCE_BODY: true, ADD_ATTR: ["target"] });
  }, [html]);

  // Catalyst-authored links to a page within this app must not do a full browser navigation: that
  // reloads the SPA from scratch, and a freshly loaded tab/window can look signed-out even though
  // the user still has a valid session. Links anywhere else (another campaustralia.com.au app, or a
  // fully external site) are left completely alone - the href is never rewritten, so no token or
  // session data ever leaves this tab.
  function handleShadowClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    const anchor = target?.closest("a");
    if (!anchor) return;

    // Only hijack a plain left-click with no modifiers, and never a link that's explicitly meant
    // to open elsewhere (target="_blank"/"_parent"/"_top" or a download) - otherwise this would
    // regress normal link behaviour (new-tab via ctrl/cmd-click, middle-click, downloads, etc).
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      (anchor.target && anchor.target.toLowerCase() !== "_self") ||
      anchor.hasAttribute("download")
    ) {
      return;
    }

    let destination: URL;
    try {
      destination = new URL(anchor.href);
    } catch {
      return;
    }

    if (destination.origin !== window.location.origin) return;

    // A same-page anchor link (e.g. href="#fees") must stay a native browser scroll-to-element -
    // ScrollRestoration scrolls to the TOP on every route change, which would otherwise hijack it.
    const current = new URL(window.location.href);
    if (destination.pathname === current.pathname && destination.search === current.search && destination.hash) {
      return;
    }

    event.preventDefault();
    navigateRef.current(destination.pathname + destination.search + destination.hash);
  }

  return <div ref={hostRef} />;
}
